/** Phase E (taroko package): Taroko Gorge and Qixingtan Beach. Lines marked “English practice” stay in English in every locale. */
export const enTaroko = {
  taroko: {
    mapNames: { hualien: 'Taroko Gorge & Qixingtan' },
    zones: {
      shrine: 'Eternal Spring Shrine',
      buluowan: 'Buluowan Terrace',
      shakadang: 'Shakadang Trail',
      qingshui: 'Qingshui Cliffs',
      village: 'Qixingtan village',
      visitor: 'Taroko Visitor Centre',
    },
    objects: {
      tk_visitor: 'Visitor centre',
      tk_shuttleTaroko: 'Tourist Shuttle stop (Taroko)',
      tk_shuttleBeach: 'Tourist Shuttle stop (Qixingtan)',
      tk_cimu: 'Cimu Bridge pavilion',
      tk_rockSign: 'Falling-rock sign',
      tk_swallows: 'Swallow Grotto board',
      tk_shrine: 'Eternal Spring Shrine',
      tk_bell: 'Bell tower',
      tk_buluowan: 'Buluowan sign',
      tk_weave: 'Truku loom',
      tk_monkeys: 'Monkey warning sign',
      tk_mochi: 'Millet mochi stall',
      tk_shakadang: 'Shakadang Trail sign',
      tk_bikeRent: 'Bike rental',
      tk_skip: 'Flat stones',
      tk_sunrise: 'Dawn Bridge',
      tk_stargaze: 'Star-gazing deck',
      tk_museum: 'Bonito museum',
      tk_sausage: 'Flying-fish sausage stall',
    },
    npc: {
      tk_guide: 'Tour guide',
      tk_rangerB: 'Trail ranger',
      tk_weaver: 'Truku weaver',
      tk_mochiAuntie: 'Truku auntie',
      tk_fisher: 'Fisherman',
      tk_kid1: 'Stone-skipping boy',
      tk_kid2: 'Stone-skipping girl',
      tk_cyclist: 'Cyclist',
      tk_photographer: 'Photographer',
      tk_bonito: 'Museum guide',
      tk_stargazer: 'Star guide',
    },
    items: {} as Record<string, { name: string; desc: string }>,

    common: {
      firstStar: '⭐ New discovery!',
      notHere: 'Hmm, nothing happens.',
    },
    visitor: {
      text: 'Taroko National Park Visitor Centre. A big 3D map shows the gorge: 19 km of marble cliffs carved by the Liwu River.',
      ask: '🗺️ Ask for directions (English)',
      q: 'How do you ask the way politely?',
      // English practice
      a: ['Excuse me, how do I get to the Shakadang Trail?', 'Trail where? Tell me.', 'Shakadang now, go go go!'],
      right: '“Walk west along the river and cross the red bridge. The stairs go down to the trail!”',
      wrong: 'The volunteer smiles: “Try a more polite way — start with ‘Excuse me…’”',
    },
    cimu: {
      text: 'Cimu Bridge (慈母橋): “the bridge of the loving mother”. A mother waited here every day for her son, who was building the mountain road. The bridge was named for her love.',
      listen: '🌉 Look at the red bridge',
      looked: 'Two white marble lions guard the bridge. Below, the river is white and fast.',
      // English practice
      phrase: 'Thank you, Mom! I love you.',
    },
    rock: {
      text: 'A yellow triangle with falling rocks on it. Rocks can fall from the cliffs at any time.',
      q: 'What does this sign mean?',
      // English practice
      a: ['Watch out for falling rocks!', 'Free rocks — take one!', 'A rock concert tonight.'],
      right: 'Exactly! Keep your helmet on and don’t stop under the cliffs.',
      wrong: 'Look again: rocks are falling down the slope…',
    },
    swallows: {
      text: 'Swallow Grotto (燕子口): the river carved hundreds of little holes in the marble, and swallows build their nests inside!',
      count: '🐦 Count the swallows',
      q: (birds: string) => `${birds}\n\nHow many swallows can you see?`,
      right: 'Well counted! Swallows fly back here every spring.',
      wrong: 'Count again, slowly: one, two, three…',
      // English practice: number words.
      numbers: ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'],
    },
    shrine: {
      text: 'The Eternal Spring Shrine (長春祠) remembers the 226 workers who died building the Central Cross-Island Highway. The spring under it never stops flowing — that’s why it is “eternal”.',
      bow: '🙏 Bow quietly',
      bowed: 'You stand still for a moment. The waterfall roars below the red roof.',
      // English practice
      phrase: '“Eternal” means “forever”.',
    },
    bell: {
      text: 'A bell tower at the top of the steep stairs. You can see the whole gorge from here!',
      ring: '🔔 Ring the bell',
      rang: 'Dong… dong… The sound echoes from cliff to cliff.',
      // English practice
      phrase: 'I rang the bell. (ring → rang → rung)',
    },
    buluowan: {
      text: 'Buluowan (布洛灣) is a terrace where Truku families lived and farmed. In the Truku language, Buluowan means “echo”.',
      shout: '📣 Shout “Hello!” across the gorge',
      // English practice
      hello: 'Hello!',
      echo: '“Hello… hello… hello…” The mountains answer you!',
    },
    weave: {
      text: 'A Truku backstrap loom. The red diamond pattern is called “the eyes of the ancestors” — it means the family is watched over and safe.',
      try: '🧵 Learn the word (English)',
      q: 'What is she doing?',
      // English practice
      a: ['She is weaving cloth.', 'She is eating cloth.', 'She is sleeping on cloth.'],
      right: 'Yes! “Weave” — she weaves red and white threads into cloth.',
      wrong: 'Look at her hands moving the threads…',
    },
    monkeys: {
      text: 'Formosan rock monkeys (台灣獼猴) live on these cliffs. They are wild — and very interested in your snacks!',
      q: 'A monkey looks at your mochi. What do you do?',
      // English practice
      a: ['Don’t feed the monkeys. I keep my food in my bag.', 'I give it all my snacks!', 'I try to touch it.'],
      right: 'Right! Human food makes wild monkeys sick and grabby.',
      wrong: 'Hmm… monkeys can bite, and our food is bad for them. Try again!',
    },
    // English practice: food order.
    mochi: { vendor: 'Millet mochi, made with our Truku millet! Peanut or plain?', ok: 'Two peanut ones, please. How much are they?', bad: ['Mochi. Give.', 'I want everything, no money.'], done: 'Soft and chewy, with a nutty millet taste. The auntie waves: “Enjoy the trail!”', price: 30 },
    shakadang: {
      text: 'Shakadang Trail (砂卡礑步道): a path cut into the cliff above a crystal-clear stream. “Skadang” is the Truku word for a molar tooth!',
      look: '💧 Look at the water',
      q: 'What colour is the water?',
      // English practice
      a: ['It’s blue-green and very clear.', 'It’s brown and muddy.', 'It’s bright red.'],
      right: 'Yes! You can see every white marble pebble on the bottom.',
      wrong: 'Look closely at the stream…',
    },
    bike: {
      text: 'Bike rental: the cycle path runs along the whole Qixingtan coast.',
      ride: '🚲 Rent a bike (English)',
      q: 'How do you ask?',
      // English practice
      a: ['I’d like to rent a bike for one hour, please.', 'Bike. One. Now.', 'Give me your bike.'],
      right: 'You ride along the coast with the wind in your hair. Mountains on the left, the Pacific on the right!',
      wrong: 'The owner laughs: “Could you say that more politely?”',
      price: 20,
      poor: 'You don’t have enough coins.',
    },
    skip: {
      text: 'Flat, smooth pebbles — perfect for skipping on the waves.',
      throw: '🥏 Throw a stone',
      again: '🥏 Try again',
      result: (n: number, words: string) => (n === 0 ? 'Plop! It sank straight away.' : `Skip, skip… it skipped ${n} time${n === 1 ? '' : 's'}! ${words}`),
      great: 'Amazing! The kids cheer for you.',
    },
    sunrise: {
      text: 'The Dawn Bridge (曙光橋). Hualien is one of the first places in Taiwan to see the sun rise over the Pacific.',
      early: '🌅 Take a sunrise photo',
      photo: '📸 Click! The sun comes up out of the ocean, orange and gold. What a photo!',
      late: 'The sun is already high. Come back before 7 a.m. for the sunrise photo!',
      // English practice
      phrase: 'The sun rises in the east.',
    },
    stars: {
      text: 'A star-gazing deck. Qixingtan (七星潭) means “Seven Star Lake”, after the seven stars of the Big Dipper.',
      look: '✨ Look at the sky',
      day: 'It’s daytime — the stars are hiding. Come back after dark!',
      q: 'How many stars are in the Big Dipper?',
      // English practice
      a: ['Seven.', 'Three.', 'Twenty.'],
      right: 'Seven! Follow the two stars at the end of the “spoon” and you find the North Star.',
      wrong: 'Count the stars in the spoon shape again…',
    },
    museum: {
      text: 'Chihsing Tan Katsuo Museum (柴魚博物館). Qixingtan was a fishing village; the fishermen smoked and dried bonito until it was as hard as wood.',
      learn: '🐟 Learn a fact',
      q: 'Why is bonito called “firewood fish” (柴魚) in Chinese?',
      // English practice
      a: ['Because dried bonito is as hard as wood.', 'Because it lives in trees.', 'Because it is cooked on a campfire only.'],
      right: 'Right! Then it is shaved into thin flakes that “dance” on hot tofu.',
      wrong: 'Hint: knock on a dried bonito — tok tok!',
    },
    // English practice: food order.
    sausage: { vendor: 'Flying-fish sausage! A Hualien speciality. With garlic?', ok: 'Yes, please. One sausage with garlic.', bad: ['Sausage! Fast!', 'Fish. Garlic. Mine.'], done: 'Smoky, juicy and a little salty, like the sea. Perfect after a swim!', price: 35 },

    talk: {
      ranger: {
        intro: 'Welcome to Taroko! Before the gorge trails, a quick safety question.',
        q: 'What must you wear on the gorge trails?',
        // English practice
        a: ['A helmet.', 'Flip-flops.', 'Sunglasses only.'],
        right: 'Good! Borrow one at the helmet booth — it’s free.',
        wrong: 'Think about falling rocks…',
      },
      tk_guide: [
        'Please follow me and stay together! This way, everyone! (English practice)',
        'Taroko means “magnificent and beautiful” in the Truku language.',
        'Our bus leaves at three o’clock. Don’t be late!',
      ],
      tk_rangerB: [
        'No stopping under the cliffs, please. Keep walking.',
        'English practice: “Watch out!” means “be careful, something is coming!”',
        'The marble here was once sea floor, millions of years ago.',
      ],
      tk_weaver: [
        'My grandmother taught me to weave. Red is for life and courage.',
        'English practice: “What are you making?” — “I’m making a bag.”',
        'In the old days, a Truku girl learned to weave before she married.',
      ],
      tk_mochiAuntie: [
        'Millet grows well in the mountains. We make wine and mochi from it.',
        'English practice: “How much is it?” — “Thirty dollars.”',
      ],
      tk_fisher: [
        'I go out before sunrise. The fish are hungry in the morning!',
        'English practice: “Did you catch anything?” — “Yes, three fish!”',
        'The sea here gets deep very fast. Don’t swim — just paddle your feet.',
      ],
      tk_kid1: [
        'My record is seven skips! Can you beat it?',
        'Find a flat stone and throw it low and fast. Like this!',
      ],
      tk_kid2: [
        'English practice: “Your turn!” — “Okay, here I go!”',
        'We stack stones too. Mine is a tower of ten!',
      ],
      tk_cyclist: [
        'English practice: “On your left!” — that’s what you say when you pass someone.',
        'The cycle path goes all the way to the city. Twenty kilometres!',
      ],
      tk_photographer: [
        'The Qingshui Cliffs are a thousand metres tall. My camera can’t fit them all in!',
        'English practice: “Could you take a photo of us, please?”',
        'Come at sunrise: the sea turns pink and gold.',
      ],
      tk_bonito: [
        'English practice: “Would you like to try some?” — “Yes, please!”',
        'Bonito flakes are smoked for many days. Smell that?',
      ],
      tk_stargazer: [
        'Look up! That bright one is not a star — it’s a planet.',
        'English practice: “Can you see the Big Dipper?” — “Yes, I can!”',
        'Far from city lights, you can see the Milky Way here.',
      ],
    },
  },
};
